import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import { ButtonLink } from '../ButtonLink'
import { LinksListExpandable } from '../LinkBlock'
import { NavigationLink } from '../Links'
import type { AspectRatio } from '../Picture/'
import { Picture } from '../Picture/'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './ToolSignpost.module.scss'

export const ToolSignpost: FunctionComponent<Props> = ({
  button,
  description,
  image,
  imageAspectRatio = 'two-to-one',
  link,
  title,
  className,
  captionText,
  linksListExpandableData,
  ...rest
}) => {
  const pictureStylesMapper = {
    'one-to-one': styles.pictureOneToOne,
    'two-to-one': styles.pictureTwoToOne,
  }

  const toolSignPostButton = button || { text: '', href: '' }
  const {
    text: buttonText,
    href: buttonHref,
    ...buttonProps
  } = toolSignPostButton

  const toolSignPostLink = link || { text: '', href: '' }
  const { text: LinkText, href: LinkHref, ...linkProps } = toolSignPostLink

  return (
    <section
      className={classnames(styles.container, className)}
      data-testid="tool-signpost"
      {...rest}
    >
      <div className={styles.contentFlexItem}>
        <div className={styles.contentContainer}>
          {title && (
            <TypographyV2
              tag="h2"
              className={styles.title}
              textStyle="sb-text-heading-standfirst"
            >
              {title}
            </TypographyV2>
          )}
          {description && (
            <TypographyV2
              tag="p"
              className={styles.description}
              textStyle="sb-text-body-default-regular"
            >
              {description}
            </TypographyV2>
          )}
          {linksListExpandableData && (
            <div className={styles.linkListContainer}>
              <LinksListExpandable {...linksListExpandableData} />
            </div>
          )}
          {buttonHref && (
            <ButtonLink
              className={styles.cta}
              href={buttonHref}
              {...buttonProps}
            >
              {buttonText}
            </ButtonLink>
          )}
          {captionText && (
            <TypographyV2
              textStyle="sb-text-body-small-regular"
              className={styles.caption}
            >
              {captionText}
            </TypographyV2>
          )}
          {LinkHref && (
            <div className={styles.navigationLink}>
              <NavigationLink
                className={styles.secondaryLink}
                href={LinkHref}
                {...linkProps}
              >
                {LinkText}
              </NavigationLink>
            </div>
          )}
        </div>
      </div>
      {image?.src && (
        <Picture
          src={image.src}
          alt={image.alt}
          sources={image.sources}
          className={classnames(
            styles.picture,
            pictureStylesMapper[imageAspectRatio]
          )}
        />
      )}
    </section>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  title?: string
  description?: string
  image?: {
    src: string
    alt: string
    sources?: Array<{ srcset: string; sizes: string; media?: string }>
  }
  imageAspectRatio?: Extract<AspectRatio, 'one-to-one' | 'two-to-one'>
  button?: Link | null
  link?: Link
  className?: string
  /**
   * Optionally renders a list of links via the LinksListExpandable component.
   */
  linksListExpandableData?: Parameters<typeof LinksListExpandable>[0]
  /**
   * Disclaimer/legal text for below the button
   */
  captionText?: string
  [key: `data-${string}`]: string | undefined
} & ComponentPropsWithoutRef<'section'>

type Link = {
  text: string
  href: string
  [key: `data-${string}`]: string | undefined
}
